/**
 * 참석 예정자 / 참석자 목록 다이얼로그.
 *
 * v1 원본: RenewalComponents/DefaultDeviceSetting/Participants/ParticipantListDialog
 * HTML 마크업: markup/asjoinpopup.html
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ParticipantItem } from '@/types/components';
import { isValidEmail, maskEmail } from '@/utils/formatUtils';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';

// re-export for backwards compat
export type { ParticipantItem } from '@/types/components';

type SortType = 'ATTEND' | 'ALPHA';

interface Props {
  open: boolean;
  /** 다이얼로그 타이틀: 'intended' → 참석 예정자 / 'conference' → 참석자 */
  type: 'intended' | 'conference';
  participantList: ParticipantItem[];
  onClose: () => void;
}

// ── 유틸 ──

const PROFILE_ICONS = {
  user: { img: 'https://static.wehago.com/imgs/common/profile_icon_user.png', color: '#c1cbdd' },
  email: { img: 'https://static.wehago.com/imgs/common/profile_icon_mail.png', color: '#b1a7ff' },
  phone: { img: 'https://static.wehago.com/imgs/common/profile_icon_phone.png', color: '#a1bff7' },
} as const;

const isPhoneNumber = (str: string) => /^[\d\-+() ]+$/.test(str);

const getProfileInfo = (item: ParticipantItem) => {
  const { user, user_name, user_type, profile_url, full_path, rank_name } = item;

  // 로그인 사용자 — 타이틀은 '이름 직급', 설명은 소속 경로
  if (user_type === 1) {
    const bgUrl = toAbsoluteProfileUrl(profile_url) || 'https://static.wehago.com/imgs/common/no_profile.png';
    const title = [user_name, rank_name].filter(Boolean).join(' ');
    return { isIcon: false, bgUrl, title, desc: full_path };
  }

  // 비로그인 사용자
  if (user_type === 2) {
    if (isValidEmail(user)) {
      return { isIcon: true, bgUrl: PROFILE_ICONS.email.img, bgColor: PROFILE_ICONS.email.color, title: user_name, desc: maskEmail(user) };
    }
    if (isPhoneNumber(user)) {
      return { isIcon: true, bgUrl: PROFILE_ICONS.phone.img, bgColor: PROFILE_ICONS.phone.color, title: user_name };
    }
    return { isIcon: true, bgUrl: PROFILE_ICONS.user.img, bgColor: PROFILE_ICONS.user.color, title: user_name };
  }

  // 메일 초대 사용자
  if (user_type === 'email') {
    return { isIcon: true, bgUrl: PROFILE_ICONS.email.img, bgColor: PROFILE_ICONS.email.color, title: maskEmail(user_name), desc: maskEmail(user_name) };
  }

  // fallback
  return { isIcon: true, bgUrl: PROFILE_ICONS.user.img, bgColor: PROFILE_ICONS.user.color, title: user_name };
};

// ── 정렬 ──

const sortAttend = (list: ParticipantItem[]) =>
  [...list].sort((a, b) => {
    if (a.is_joined && !b.is_joined) return -1;
    if (!a.is_joined && b.is_joined) return 1;
    if (a.is_master && !b.is_master) return -1;
    if (!a.is_master && b.is_master) return 1;
    return (a.user_name || '').localeCompare(b.user_name || '', 'ko', { numeric: true, sensitivity: 'base' });
  });

const sortAlpha = (list: ParticipantItem[]) =>
  [...list].sort((a, b) =>
    (a.user_name || '').localeCompare(b.user_name || '', 'ko', { numeric: true, sensitivity: 'base' }),
  );

// ── 컴포넌트 ──

const SORT_OPTIONS: Array<{ id: SortType; name: string }> = [
  { id: 'ATTEND', name: '참석순' },
  { id: 'ALPHA', name: '가나다순' },
];

const ParticipantListDialog: React.FC<Props> = ({ open, type, participantList, onClose }) => {
  const [sort, setSort] = useState<SortType>('ATTEND');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // ESC 닫기
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  // 정렬 드롭다운 외부 클릭 닫기
  const closeSortDrop = useCallback(() => setIsSortOpen(false), []);

  const sorted = sort === 'ATTEND' ? sortAttend(participantList) : sortAlpha(participantList);
  const titleText = type === 'conference' ? '참석자' : '참석 예정자';
  const currentSortName = SORT_OPTIONS.find((s) => s.id === sort)?.name ?? '참석순';

  if (!open) return null;

  return (
    <div className="LUX_basic_dialog" style={{ display: 'block' }}>
      <div className="dimmed" onClick={onClose} />
      <div className="dialog_wrap_tbl">
        <div className="dialog_wrap_tbl">
          <div className="dialog_wrap_tblcel">
            <div
              ref={dialogRef}
              className="dialog_wrap"
              style={{ border: 0, borderRadius: '10px', margin: '5px' }}
            >
              <div className="dialog_content rtc_dialog rtc_new4_dialog roundstyle" style={{ width: 380 }}>
                <div className="dialog_data">
                  {/* 헤더 */}
                  <div className="dialog_data_tit">
                    <h1 className="txtcnt">{titleText}</h1>
                    <button type="button" className="LUX_basic_btn btn_clr" onClick={onClose}>
                      <span className="sp_lux">닫기</span>
                    </button>
                  </div>

                  {/* 본문 */}
                  <div className="dialog_data_area noline" style={{padding:0, border:0}}>
                    <div className="rtcDialog_attendListBox">
                      {/* 상단: 전체 N건 + 정렬 */}
                      <div className="rtcDialog_attendListBox__top">
                        <div className="leftBox">
                          <p className="leftBoxText">전체 <em>{sorted.length}</em>명</p>
                        </div>
                        <div className="rightBox">
                          <div className={`common_component_select noline${isSortOpen ? ' open' : ''}`}>
                            <button
                              type="button"
                              className="selected_item"
                              onClick={() => setIsSortOpen((v) => !v)}
                            >
                              {currentSortName}
                            </button>
                            <div className="common_component_dropdown notosans_font" style={{ width: 84 }}>
                              <ul className="common_component_dropdownList">
                                {SORT_OPTIONS.map((opt) => (
                                  <li key={opt.id}>
                                    <button
                                      type="button"
                                      className="common_component_dropdownBtn ellipsis"
                                      onClick={() => { setSort(opt.id); setIsSortOpen(false); }}
                                    >
                                      <span>{opt.name}</span>
                                      {sort === opt.id && (
                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M14.64 3.987a.75.75 0 0 1 1.095 1.025l-8.438 9a.75.75 0 0 1-1.11-.018L2.249 9.515a.75.75 0 0 1 1.127-.99l3.391 3.859 7.873-8.397z" fill="#4A4A4A" />
                                        </svg>
                                      )}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          {/* 드롭다운 외부 클릭 배경 */}
                          {isSortOpen && (
                            <div
                              onClick={closeSortDrop}
                              style={{
                                position: 'fixed', left: 0, top: 0,
                                width: '100%', height: '100%',
                                background: 'transparent', zIndex: 1,
                              }}
                            />
                          )}
                        </div>
                      </div>

                      {/* 목록 */}
                      <ul className="rtcDialog_attendList common_customScrollbar">
                        {sorted.map((item, idx) => {
                          const info = getProfileInfo(item);
                          return (
                            <li key={item.user || idx}>
                              <div className="rtcDialog_attendList__leftBox">
                                <div
                                  className={`common_coProfileBox hasOnlineStat${info.isIcon ? ' common_coProfileBox--icon' : ''}${!item.is_joined ? ' dimmed_custom' : ''}`}
                                  style={info.bgColor ? { background: info.bgColor } : undefined}
                                >
                                  <div
                                    className="common_coProfileBox__imgBox"
                                    style={{ backgroundImage: `url(${info.bgUrl})` }}
                                  />
                                  {item.is_joined ? (
                                    <span className="common_coProfileBox__online">온라인</span>
                                  ) : (
                                    <span className="common_coProfileBox__offline">오프라인</span>
                                  )}
                                </div>
                              </div>
                              <div className="rtcDialog_attendList__rightBox">
                                <strong className="rtcDialog_attendList__rightBoxTitle ellipsis">
                                  {info.title}
                                </strong>
                                {info.desc && (
                                  <p className="rtcDialog_attendList__rightBoxDesc ellipsis">
                                    {info.desc}
                                  </p>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              {/* 확인 버튼 */}
              <div className="dialog_btnbx">
                <div>
                  <button
                    type="button"
                    className="common_round_btn rtcNew_btnType blue"
                    onClick={onClose}
                  >
                    확인
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantListDialog;
